import type { DsTone } from 'src/auditoria/components/ds';
import type { GridColDef } from '@mui/x-data-grid';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

import { TxDataGridClient } from 'src/auditoria/components/tx-data-grid/TxDataGridClient';
import {
  DsNum,
  DsButton,
  colunaLink,
  DsCellLink,
  GradeCanvas,
  DsStatusChip,
  DS_BOX_RADIUS,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr } from '../Nfse/types';

// ----------------------------------------------------------------------
// Peças compartilhadas pelos dois artboards do DTE — `portal-dte` e
// `portal-dte-detalhe-protocolo-2026-004807` são a MESMA tela: cabeçalho, a
// grade de notificações à esquerda e o rail de 300px à direita. O detalhe só
// acrescenta o card da mensagem abaixo da grade e marca a linha aberta. Por
// isso a grade e o rail vivem aqui, e não duplicados em List/Detail.
// ----------------------------------------------------------------------

export interface MensagemDte {
  id: number;
  numero: number | null;
  titulo: string;
  tipo: string;
  tipoCodigo: number;
  dataPostagem: string | null;
  dataLimite: string | null;
  lida: boolean;
  temProtocolo: boolean;
  exigeCiencia: boolean;
}

export interface DteResposta {
  mensagens: MensagemDte[];
  naoLidas: number;
}

/**
 * Tom do chip de tipo, literal do artboard: Intimação `.cs-error`,
 * Notificação `.cs-warning`, Aviso `.chip` (o tom primary do canvas).
 */
export function toneTipo(tipoCodigo: number): DsTone {
  if (tipoCodigo === 3) return 'error';
  if (tipoCodigo === 2) return 'warning';
  return 'primary';
}

/** Chip de situação: `Não lida` em warning, `Lida`/`Ciência dada` em success. */
function chipSituacao(row: MensagemDte) {
  if (!row.lida) return <DsStatusChip tone="warning">Não lida</DsStatusChip>;
  return <DsStatusChip tone="success">{row.temProtocolo ? 'Ciência dada' : 'Lida'}</DsStatusChip>;
}

// ----------------------------------------------------------------------
// Grade
// ----------------------------------------------------------------------

/** Larguras do `.thead` do artboard: 110 / 70 / 1fr / 100 / 100 / 120. */
function colunas(aoAbrir: (row: MensagemDte) => void): GridColDef<MensagemDte>[] {
  return [
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <DsStatusChip tone={toneTipo(p.row.tipoCodigo)}>{p.row.tipo}</DsStatusChip>
      ),
    },
    {
      field: 'numero',
      headerName: 'Nº',
      width: 70,
      renderCell: (p) => <DsNum mono>{p.row.numero ?? '—'}</DsNum>,
    },
    // Convenção do canvas: exatamente UMA coluna clicável por linha, a primeira
    // textual. Abrir a mensagem é o que registra a ciência.
    colunaLink({ field: 'titulo', headerName: 'Assunto', flex: 1, minWidth: 240 }, aoAbrir),
    {
      field: 'dataPostagem',
      headerName: 'Recebida',
      width: 100,
      renderCell: (p) => <DsNum mono>{formatDateBr(p.row.dataPostagem)}</DsNum>,
    },
    {
      field: 'dataLimite',
      headerName: 'Prazo',
      width: 100,
      renderCell: (p) => (
        <DsNum mono>{p.row.dataLimite ? formatDateBr(p.row.dataLimite) : '—'}</DsNum>
      ),
    },
    {
      field: 'lida',
      headerName: 'Situação',
      width: 120,
      renderCell: (p) => chipSituacao(p.row),
    },
  ];
}

/** `.trow[aria-current]` do artboard de detalhe: fundo selecionado + filete primary. */
const SX_LINHA_ABERTA = {
  '& .dte-linha-aberta': {
    bgcolor: 'action.selected',
    boxShadow: 'inset 3px 0 0 var(--palette-primary-main)',
  },
};

export type GradeNotificacoesProps = {
  mensagens: MensagemDte[];
  carregando?: boolean;
  /** Id da mensagem aberta — só o artboard de detalhe passa. */
  abertaId?: number;
  aoAbrir: (row: MensagemDte) => void;
};

export function GradeNotificacoes({
  mensagens,
  carregando,
  abertaId,
  aoAbrir,
}: GradeNotificacoesProps) {
  return (
    <GradeCanvas sx={SX_LINHA_ABERTA}>
      <TxDataGridClient<MensagemDte>
        rows={mensagens}
        columns={colunas(aoAbrir)}
        getRowId={(row) => row.id}
        // Dentro do casulo o `Paper` do TxDataGrid perde o `height:100%` útil
        // (o `Card` tem altura automática) e a grade colapsaria. `autoHeight` é
        // também o que o canvas desenha: o card cresce com as linhas.
        autoHeight
        hideToolbar
        hideFooter
        // Sem rodapé não há como paginar: o padrão de 10 do `TxDataGrid`
        // escondia notificação da caixa de entrada sem nenhum aviso.
        initialPageSize={100}
        getRowClassName={(p) => (p.row.id === abertaId ? 'dte-linha-aberta' : '')}
        // `TxDataGridClient` fecha `slots` e `loading` no tipo — o vazio é dito
        // pelo `localeText`, em português, em vez de um overlay próprio.
        localeText={{
          noRowsLabel: carregando ? 'Carregando notificações…' : 'Nenhuma notificação recebida.',
        }}
        {...SX_GRADE_CANVAS}
      />
    </GradeCanvas>
  );
}

// ----------------------------------------------------------------------
// Rail de 300px — "Ciência e protocolo"
// ----------------------------------------------------------------------

type EstadoPasso = 'feito' | 'atual' | 'proximo';

/**
 * `.stepdot` do artboard. §1 manda usar `Stepper` do MUI no lugar de `.stepdot`,
 * mas aqui não há conector, navegação nem passo corrente de verdade: são três
 * linhas de legenda sobre como a ciência funciona. Um `Stepper` traria
 * `StepConnector` e semântica de progresso que a tela não tem. Registrado no
 * mailbox do lote.
 */
function Passo({
  estado,
  numero,
  titulo,
  texto,
}: {
  estado: EstadoPasso;
  numero: number;
  titulo: string;
  texto: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        mb: '12px',
      }}
    >
      <Box
        sx={[
          {
            width: 26,
            height: 26,
            flex: '0 0 26px',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
            fontWeight: 800,
          },
          estado === 'feito' && {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          },
          estado === 'atual' && {
            border: '2px solid',
            borderColor: 'primary.main',
            color: 'primary.dark',
            bgcolor: 'transparent',
          },
          estado === 'atual' &&
            ((theme) =>
              theme.applyStyles('dark', {
                color: theme.vars.palette.primary.light,
              })),
          estado === 'proximo' && {
            bgcolor: 'background.neutral',
            color: 'text.disabled',
          },
        ]}
      >
        <DsNum>{numero}</DsNum>
      </Box>

      <Box sx={{ fontSize: 12 }}>
        <Box component="b">{titulo}</Box>
        <Box sx={{ color: 'text.secondary' }}>{texto}</Box>
      </Box>
    </Box>
  );
}

/** `.kv`: rótulo à esquerda, número tabular à direita, filete tracejado. */
function LinhaKv({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        borderBottom: '1px dashed',
        borderColor: 'divider',
        fontSize: 12,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {rotulo}
      </Box>
      <Box component="b">
        <DsNum>{valor}</DsNum>
      </Box>
    </Box>
  );
}

/** Prazo a vencer = tem data limite no futuro e a ciência ainda não foi dada. */
function prazoEmAberto(m: MensagemDte): boolean {
  if (!m.dataLimite || m.temProtocolo) return false;
  const limite = new Date(m.dataLimite);
  return !Number.isNaN(limite.getTime()) && limite.getTime() >= Date.now();
}

export type RailCienciaProps = {
  mensagens: MensagemDte[];
  naoLidas: number;
  aoAbrirProtocolo: () => void;
};

export function RailCiencia({ mensagens, naoLidas, aoAbrirProtocolo }: RailCienciaProps) {
  const cienciaDada = mensagens.filter((m) => m.temProtocolo).length;
  const prazoAVencer = mensagens.filter(prazoEmAberto).length;

  return (
    <Card sx={{ flex: '0 0 300px', padding: '18px 20px' }}>
      <Box sx={{ fontSize: 13, fontWeight: 800, mb: '10px' }}>Ciência e protocolo</Box>

      <Passo
        estado="feito"
        numero={1}
        titulo="Abrir registra ciência"
        texto="Sem botão — o GET do detalhe já retorna o protocolo."
      />
      <Passo
        estado="atual"
        numero={2}
        titulo="Protocolo de Recebimento"
        texto="Emitido p/ Notificação e Intimação; Aviso só marca leitura."
      />
      <Passo
        estado="proximo"
        numero={3}
        titulo="Contestar pelo Protocolo"
        texto="Assunto sugerido pelo fiscal, cadastro pré-preenchido."
      />

      <Box sx={{ height: '1px', bgcolor: 'divider', margin: '12px 0' }} />

      <LinhaKv rotulo="Mensagens" valor={mensagens.length} />
      <LinhaKv rotulo="Não lidas" valor={naoLidas} />
      <LinhaKv rotulo="Ciência dada" valor={cienciaDada} />
      <LinhaKv rotulo="Prazo a vencer" valor={prazoAVencer} />

      <DsButton
        tone="primary"
        size="tall"
        onClick={aoAbrirProtocolo}
        sx={{ width: '100%', justifyContent: 'center', mt: '12px' }}
      >
        Abrir protocolo
      </DsButton>
    </Card>
  );
}

// ----------------------------------------------------------------------
// Card da mensagem — o segundo bloco do artboard, abaixo da grade
// ----------------------------------------------------------------------

export interface ProtocoloRecebimento {
  numero: number;
  codigoVerificacao: string;
  dataHoraEmissao: string;
}

export interface DetalheDte {
  id: number;
  numero: number | null;
  titulo: string;
  conteudo: string | null;
  tipo: string;
  tipoCodigo: number;
  dataPostagem: string | null;
  dataLimite: string | null;
  lida: boolean;
  exigeCiencia: boolean;
  fundamentacao: string | null;
  sugestaoProtocolo: { assuntoId: number; assuntoNome: string | null } | null;
  protocolo: ProtocoloRecebimento | null;
}

/** `.divider` do artboard: filete de 1px em `divider`. */
const SX_FILETE = {
  height: '1px',
  bgcolor: 'divider',
  margin: '10px 0',
} as const;

export type CardMensagemProps = {
  detalhe: DetalheDte;
  /** Abre o Cadastrar Processo já preenchido com o assunto sugerido pelo fiscal. */
  aoContestar: (assuntoId: number) => void;
  /** Só o artboard de detalhe mostra o "‹ Voltar às notificações". */
  aoVoltar?: () => void;
};

export function CardMensagem({ detalhe, aoContestar, aoVoltar }: CardMensagemProps) {
  const sugestao = detalhe.sugestaoProtocolo;
  const temLinkNoCorpo = (detalhe.conteudo ?? '').includes('data-abrir-protocolo');

  return (
    <Card sx={{ padding: '16px 18px', mt: '12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mb: '8px',
          flexWrap: 'wrap',
        }}
      >
        <DsStatusChip tone={toneTipo(detalhe.tipoCodigo)}>{detalhe.tipo}</DsStatusChip>

        {detalhe.numero != null && (
          <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
            Nº <DsNum mono>{detalhe.numero}</DsNum>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
          Recebida em <DsNum mono>{formatDateBr(detalhe.dataPostagem)}</DsNum>
        </Box>
      </Box>

      <Box sx={{ fontSize: 15, fontWeight: 800 }}>{detalhe.titulo}</Box>

      {detalhe.dataLimite && (
        <Box sx={{ fontSize: 12, color: 'text.secondary', margin: '4px 0 8px' }}>
          Prazo:{' '}
          <Box component="b">
            <DsNum mono>{formatDateBr(detalhe.dataLimite)}</DsNum>
          </Box>
        </Box>
      )}

      <Box sx={SX_FILETE} />

      {/*
        Conteúdo autoral do fisco (mesma base, interno). O link de contestação
        vem dentro do corpo com `[data-abrir-protocolo]`: o clique é
        interceptado aqui e leva ao Cadastrar Processo pré-preenchido.
      */}
      <Box
        sx={{ fontSize: 13, lineHeight: 1.7, '& p': { my: '8px' } }}
        onClick={(e) => {
          const alvo = (e.target as HTMLElement).closest('[data-abrir-protocolo]');
          if (alvo && sugestao) {
            e.preventDefault();
            aoContestar(sugestao.assuntoId);
          }
        }}
        dangerouslySetInnerHTML={{ __html: detalhe.conteudo ?? '' }}
      />

      {detalhe.fundamentacao && (
        <Box component="p" sx={{ fontSize: 12, color: 'text.secondary' }}>
          <Box component="b">Fundamentação legal:</Box> {detalhe.fundamentacao}
        </Box>
      )}

      {/*
        Mensagem manual com assunto sugerido mas sem o link no corpo: mesmo
        destino, como texto-link — o canvas não põe botão aqui.
      */}
      {sugestao && !temLinkNoCorpo && (
        <Box sx={{ fontSize: 12, fontWeight: 700 }}>
          <DsCellLink onClick={() => aoContestar(sugestao.assuntoId)}>
            Deseja contestar
            {sugestao.assuntoNome ? ` (${sugestao.assuntoNome})` : ''}? O cadastro já vem
            preenchido.
          </DsCellLink>
        </Box>
      )}

      <Box sx={SX_FILETE} />

      {detalhe.protocolo ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'success.main',
            borderRadius: `${DS_BOX_RADIUS}px`,
            padding: '10px 14px',
            mt: '10px',
            fontSize: 12,
            color: 'text.primary',
          }}
        >
          <Box component="b">Ciência registrada — Protocolo de Recebimento</Box>
          <br />
          Nº{' '}
          <Box component="b">
            <DsNum mono>{detalhe.protocolo.numero}</DsNum>
          </Box>{' '}
          · código de verificação{' '}
          <Box component="b">
            <DsNum mono>{detalhe.protocolo.codigoVerificacao}</DsNum>
          </Box>
          {detalhe.protocolo.dataHoraEmissao ? (
            <>
              {' · '}
              <DsNum mono>{formatDateBr(detalhe.protocolo.dataHoraEmissao)}</DsNum>
            </>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          Aviso — leitura registrada. Não gera Protocolo de Recebimento.
        </Box>
      )}

      {aoVoltar && (
        <Box sx={{ mt: '10px', fontSize: 12, fontWeight: 700 }}>
          {/*
            O canvas pinta este link em `text.secondary`; o `DsCellLink` do kit é
            `info.main` e não tem prop de tom — divergência registrada no mailbox.
          */}
          <DsCellLink onClick={aoVoltar}>‹ Voltar às notificações</DsCellLink>
        </Box>
      )}
    </Card>
  );
}

/** Faixa da tela: coluna elástica à esquerda, rail de 300px à direita. */
export const SX_COLUNAS = {
  display: 'flex',
  gap: '16px',
  alignItems: 'flex-start',
  flexWrap: { xs: 'wrap', md: 'nowrap' },
} as const;
